import { baseApi } from "@/shared/api/baseApi";
import { ENDPOINTS } from "@/shared/api/endpoints";
import type { LayerVersion } from "@/shared/types/layer";
import type { Notification } from "@/shared/types/notification";
import type { Shop } from "@/shared/types/shop";
import type { BillingInfo, LanguageOption, SelectedPlan, UserInfo } from "@/shared/types/user";

interface BackendLanguage {
  iso6391: string;
  name: string;
}

export const shellApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLanguages: build.query<LanguageOption[], void>({
      query: () => ENDPOINTS.languages,
      transformResponse: (response: BackendLanguage[]) =>
        response.map((item) => ({ value: item.iso6391, text: item.name })),
      providesTags: ["Languages"],
    }),
    getCustomer: build.query<BillingInfo, void>({
      query: () => ENDPOINTS.customersMe,
      providesTags: ["Customer"],
    }),
    getUser: build.query<UserInfo, void>({ query: () => ENDPOINTS.usersMe, providesTags: ["User"] }),
    getPlan: build.query<SelectedPlan | unknown[], void>({
      query: () => ENDPOINTS.customersMePlan,
      providesTags: ["Plan"],
    }),
    getShops: build.query<Shop[], void>({ query: () => ENDPOINTS.shops, providesTags: ["Shop"] }),
    getCustomersCount: build.query<Record<string, unknown>, void>({
      query: () => ENDPOINTS.customersMeCount,
    }),
    getNotificationsCount: build.query<{ unread: number }, void>({
      query: () => ENDPOINTS.notificationsCount,
      providesTags: ["Notifications"],
    }),
    getLayerVersions: build.query<LayerVersion[], void>({ query: () => ENDPOINTS.layerVersions }),
    // La lista NON ha il tag Notifications: la pagina e' accumulata localmente dalla modale, e un'invalidazione
    // (lettura di una notifica) deve aggiornare solo il contatore, come checkNotificationCounter nel legacy.
    getNotifications: build.query<Notification[], { from: number; size: number }>({
      query: ({ from, size }) => ENDPOINTS.notificationsPage(from, size),
    }),
    readNotification: build.mutation<unknown, Notification["id"]>({
      query: (id) => ({ url: ENDPOINTS.notificationRead(id), method: "POST" }),
      invalidatesTags: ["Notifications"],
    }),
    readAllNotifications: build.mutation<unknown, void>({
      query: () => ({ url: ENDPOINTS.notificationsBulkRead, method: "POST" }),
      invalidatesTags: ["Notifications"],
    }),
    updateShopLanguage: build.mutation<unknown, { id: Shop["id"]; iso6391: string }>({
      query: ({ id, iso6391 }) => ({ url: ENDPOINTS.shopLanguage(id), method: "PUT", body: { iso6391 } }),
    }),
    updateShopData: build.mutation<unknown, { id: Shop["id"]; data: Record<string, unknown> }>({
      query: ({ id, data }) => ({ url: ENDPOINTS.shopData(id), method: "PUT", body: data }),
    }),
    /** PUT della configurazione: il backend risponde con la lista aggiornata degli shop (come oggi). */
    updateShopConfiguration: build.mutation<Shop[], { id: Shop["id"]; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.shopConfiguration(id), method: "PUT", body }),
      invalidatesTags: ["Shop"],
    }),
    setShopActivation: build.mutation<unknown, { id: Shop["id"]; active: boolean }>({
      query: ({ id, active }) => ({ url: ENDPOINTS.shopActivation(id), method: active ? "POST" : "DELETE" }),
      invalidatesTags: ["Shop"],
    }),
  }),
});

export const {
  useGetLanguagesQuery,
  useGetCustomerQuery,
  useGetUserQuery,
  useGetPlanQuery,
  useGetShopsQuery,
  useGetCustomersCountQuery,
  useGetNotificationsCountQuery,
  useGetLayerVersionsQuery,
  useLazyGetNotificationsQuery,
  useReadNotificationMutation,
  useReadAllNotificationsMutation,
  useUpdateShopLanguageMutation,
  useUpdateShopDataMutation,
  useUpdateShopConfigurationMutation,
  useSetShopActivationMutation,
} = shellApi;
